import type { FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import type { FirebaseEnv } from './env';

/**
 * Google Analytics, y por qué no bastaba con el `measurementId`.
 *
 * El identificador ya viajaba dentro de la configuración de `initializeApp`, y
 * eso se lee como «analytics configurado». No lo es: es un campo más del objeto.
 * El SDK sólo arranca —y sólo entonces se carga `gtag.js`— cuando alguien llama
 * a `getAnalytics(app)`. Sin esa llamada no se envía un solo evento, y no hay
 * nada en la consola que lo delate: la propiedad de GA4 simplemente está vacía.
 *
 * **El SDK se importa en caliente, no arriba.** Así queda en un trozo aparte —34
 * kB, 7 gzip, medido— que **sólo se descarga cuando hay `measurementId`**. Con un
 * `import` normal iría dentro del paquete principal y lo pagaría todo el mundo,
 * también hoy, que no hay identificador y no se envía un solo evento.
 *
 * **Nada de esto puede tumbar el arranque.** Analytics es telemetría: si el
 * navegador lo bloquea, si no hay cookies, si la propiedad está mal enlazada, el
 * portal tiene que seguir funcionando igual. De ahí que todo devuelva `null` en
 * vez de lanzar.
 */

/**
 * Si corresponde arrancarlo, mirando sólo la configuración.
 *
 * Suelta y exportada para poder fijar la decisión sin navegador ni Firebase: es
 * la mitad que se puede equivocar, y las dos condiciones tienen motivos
 * distintos que conviene no mezclar.
 */
export const shouldStartAnalytics = (env: FirebaseEnv): boolean => {
  /* Sin identificador no hay nada que enlazar: es el estado de hoy y el que deja
     el SDK fuera del paquete. */
  if (env.measurementId === null || env.measurementId.length === 0) return false;
  /*
   * Contra emuladores, no. Los emuladores no tienen su propio Analytics, así que
   * los eventos de una sesión de pruebas irían a la **propiedad de producción** y
   * se mezclarían con los de las 335 personas, sin forma de separarlos después.
   */
  if (env.useEmulators) return false;
  return true;
};

let analytics: Analytics | null = null;

/**
 * Arranca Analytics si toca, sin bloquear el arranque del portal.
 *
 * No hace falta comprobar el modo demo: `initializeFirebase` —lo único que llama
 * aquí— no se ejecuta en demo, y un guardia de más sugeriría que sí.
 */
export const startAnalytics = async (app: FirebaseApp, env: FirebaseEnv): Promise<void> => {
  if (!shouldStartAnalytics(env)) return;

  try {
    const { getAnalytics, isSupported } = await import('firebase/analytics');
    /* Hay navegadores y contextos donde el SDK no puede funcionar —sin cookies,
       en un `file://`, en algunos modos privados—. `isSupported` es la forma
       soportada de preguntarlo; llamar a `getAnalytics` sin ella lanza. */
    if (!(await isSupported())) return;
    analytics = getAnalytics(app);
  } catch (cause) {
    console.warn('[firebase] No se pudo iniciar Analytics. El portal sigue igual.', cause);
  }
};

/**
 * Una vista de pantalla.
 *
 * Hace falta escribirlo a mano porque esto es una SPA: `getAnalytics` dispara la
 * vista de la primera carga y nada más, de modo que sin esto la propiedad
 * mediría una sola pantalla por sesión —la de entrada— y parecería que nadie
 * navega. Es la diferencia entre «activado» y «sirve de algo».
 *
 * Si Analytics no arrancó, no hace nada: quien llama no tiene que preguntar.
 */
export const trackPageView = (path: string, title: string): void => {
  if (analytics === null) return;
  void import('firebase/analytics')
    .then(({ logEvent }) => {
      logEvent(analytics as Analytics, 'page_view', {
        page_path: path,
        page_title: title,
        page_location: `${window.location.origin}${path}`,
      });
    })
    .catch(() => {
      /* Una vista perdida no se reintenta ni se reporta: es telemetría. */
    });
};

/** Sólo para las pruebas: devuelve el módulo a como estaba antes de arrancar. */
export const resetAnalyticsForTests = (): void => {
  analytics = null;
};
